<template>
    <div class="w-full">
        <div class="recordings w-full flex flex-col justify-between items-center mb-4 py-2 px-4 gap-2"
            @click="selectThisRecording">
            <div class="flex flex-row justify-between items-center w-full">
                <p class="text-lg font-semibold">
                    {{ truncateText(recording.Name, 20) }}
                </p>
                <div class="flex gap-2 justify-end items-center">
                    <!-- Processing state with timeout -->
                    <template v-if="isProcessing">
                        <span class="cursor-pointer">
                            <vx-tooltip text="/Processing storage..." position="top">
                                <div class="processing-animation">
                                    <span class="dot"></span>
                                    <span class="dot"></span>
                                    <span class="dot"></span>
                                </div>
                            </vx-tooltip>
                        </span>
                    </template>

                    <!-- Retry state after timeout -->
                    <template v-else-if="showRetry">
                        <span class="cursor-pointer" @click="retryFetch">
                            <vx-tooltip text="/Click to retry" position="top">
                                <div class="retry-button">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round">
                                        <polyline points="23 4 23 10 17 10"></polyline>
                                        <polyline points="1 20 1 14 7 14"></polyline>
                                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15">
                                        </path>
                                    </svg>
                                </div>
                            </vx-tooltip>
                        </span>
                    </template>

                    <!-- Success state -->
                    <template v-else-if="selectedStorage">
                        <span class="choose_plat cursor-pointer">
                            <vx-tooltip text="/Uploaded" position="top">
                                <img class="w-6 h-6 object-contain" :src="selectedStorage.storage.provider.image" />
                            </vx-tooltip>
                        </span>
                        <span class="down_btn cursor-pointer" @click="handleDownloadRecording">
                            <vx-tooltip text="/Download" position="top">
                                <DownloadButton />
                            </vx-tooltip>
                        </span>
                        <span>
                            <vx-tooltip text="/transcription" position="top">
                                <img class="w-6 h-6 object-contain cursor-pointer" src="@/assets/images/ai.png"
                                    @click="openVideoRecording" />
                            </vx-tooltip>
                        </span>
                    </template>
                </div>
            </div>
            <div class="flex flex-row justify-between items-center w-full">
                <div class="flex flex-col gap-2">
                    <p style="color: #a6a6a6; font-size: 16px">
                        >>Cast:
                        <span style="color: #d7df23">
                            {{ truncateText(recording.cast_name, 20) }}
                        </span>
                    </p>

                    <p style="color: #a6a6a6; font-size: 16px">
                        >>Size:
                        <span style="color: #d7df23">
                            {{ recording["Playback Data"]["Playback Size"] }}</span>
                    </p>
                </div>
                <div class="flex flex-col gap-2">
                    <p class="flex justify-end items-end" style="color: #a6a6a6; font-size: 16px">
                        {{ recording["Start Time (Readable)"].split(" ")[0] }}
                    </p>

                    <p style="color: #a6a6a6; font-size: 16px">
                        >>Length:
                        <span style="color: #d7df23">{{ recording["Playback Data"]["Playback Length"] }} min</span>
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/http/api/api.js';
import CopyButton from "./common/CopyButton.vue";
import StartButton from "./common/StartButton.vue";
import DeleteButton from "./common/DeleteButton.vue";
import DownloadButton from "./common/DownloadButton.vue";
import constants from "../../../../../constant";
import CommonLoader from "./common/CommonLoader.vue";
import { video } from "@microsoft/teams-js";

export default {
    name: "RecordingCard",
    props: ["recording", "index", "getRecordings", "siaMinutes", "siaFreeGiven", "swarmMinutes", "swarmFreeGiven"],
    data() {
        return {
            isSelecting: false,
            loading: false,
            taskId: '',
            selectedStorage: null,
            videoUrl: null,
            isProcessing: true,
            showRetry: false,
            processingTimeout: null,
            apiTimeout: null,
            retryCount: 0,
            maxRetries: 12,
        };
    },
    components: {
        CopyButton,
        StartButton,
        DeleteButton,
        DownloadButton,
        CommonLoader,
    },
    computed: {
        accessToken() {
            return this.$store.state.accessToken;
        },
    },
    mounted() {
        this.fetchSelectedStorage();
    },
    beforeDestroy() {
        clearTimeout(this.processingTimeout);
        clearTimeout(this.apiTimeout);
    },
    methods: {
        openRecording() {
            const playbackURL =
                this.recording["Playback Data"]["Playback URL"].replace(
                    "/presentation/2.3",
                    "/video"
                ) + "/video-0.m4v";
            window.open(
                playbackURL,
                "_blank",
                "width=1366,height=768,scrollbars=yes,resizable=yes"
            );
        },
        openVideoRecording() {
            const mp4URL =
                this.selectedStorage.fileUrl;

            this.$store.dispatch("cast/saveRecording", {
                recording: this.selectedStorage,
                videoUrl: mp4URL
            });

            const recordingId = this.selectedStorage.recordId;
            const routeData = this.$router.resolve({
                name: "PlayRecording",
                params: { recordingId }
            });

            // Open in a new tab
            window.open(routeData.href, '_blank');
        },
        copyRecording() {
            navigator.clipboard.writeText(
                this.recording["Playback Data"]["Playback URL"].replace(
                    "/presentation/2.3",
                    "/video"
                ) + "/video-0.m4v"
            );
        },

        editRecord() {
            setTimeout(() => {
                const meetingId = this.recording['Record ID'];
                const url = `https://beta.editor.video.wiki/studio?meetingId=${meetingId}`;
                // window.open(url, '_blank');
                window.open(url, '_blank', 'width=1366,height=768,scrollbars=yes,resizable=yes');
            }, 1000);
        },

        async updateIsDeducted() {
            const url = `${constants.apiCastUrl}/api/decast/update/deducted/status/`;
            const payload = {
                cast_id: this.recording.pub_id,
                start_time: this.recording["Start Time (Readable)"],
                l_time: this.recording["End Time (Readable)"],
                is_deducted: this.recording.is_deducted,
            };

            try {
                const response = await api.post(url, payload, {
                    headers: {
                        Authorization: `Bearer ${this.accessToken}`,
                    },
                });
                console.log("Update successful:", response.data);
            } catch (error) {
                console.error("Error updating deducted status:", error);
            }
        },

        async fetchSelectedStorage() {
            this.isProcessing = true;
            this.showRetry = false;
            this.retryCount = 0;

            if (this.retryInterval) {
                clearInterval(this.retryInterval);
            }

            this.processingTimeout = setTimeout(() => {
                if (this.selectedStorage === null) {
                    this.isProcessing = false;
                    this.showRetry = true;
                    clearInterval(this.retryInterval);
                }
            }, 90000);
            this.retryInterval = setInterval(() => {
                if (this.retryCount >= this.maxRetries || this.selectedStorage !== null) {
                    clearInterval(this.retryInterval);
                    return;
                }
                this.retryCount++;
                this.attemptFetch();
            }, 5000);

            await this.attemptFetch();
        },

        async attemptFetch() {
            const cast_id = this.recording.pub_id;
            const start_time = this.recording["Start Time (Readable)"];
            const l_time = this.recording["End Time (Readable)"];
            const url = `${constants.apiCastUrl}/api/v1/decast/get-single-decast-recording/?start_time=${start_time}&l_time=${l_time}&pub_id=${cast_id}`;

            try {
                const response = await api.get(url);

                if (response.status === 200 && response.data) {
                    this.selectedStorage = response.data;
                    console.log("Selected storage:", this.selectedStorage.recordId);
                    this.isProcessing = false;
                    this.showRetry = false;
                    clearInterval(this.retryInterval);
                    clearTimeout(this.processingTimeout);
                }
            } catch (error) {
                console.error(`Attempt ${this.retryCount} failed:`, error);
            }
        },
        async retryFetch() {
            this.isProcessing = true;
            this.showRetry = false;

            const cast_id = this.recording.pub_id;
            const start_time = this.recording["Start Time (Readable)"];
            const l_time = this.recording["End Time (Readable)"];
            const url = `${constants.apiCastUrl}/api/v1/decast/manual-decast-recording-upload/?start_time=${encodeURIComponent(start_time)}&l_time=${encodeURIComponent(l_time)}&pub_id=${cast_id}`;

            try {
                const response = await api.get(url);

                if (response.status === 202) {
                    this.fetchSelectedStorage();
                } else {
                    throw new Error('Failed to initiate manual upload');
                }
            } catch (error) {
                const status = error.response?.status;

                if (status === 400) {
                    this.$vs.notify({
                        title: 'No Storage Provider Selected',
                        text: 'Please select a storage provider to upload your recording in your Decast.',
                        color: 'warning',
                    });
                } else {
                    console.error("Error in retryFetch:", error);
                }

                this.isProcessing = false;
                this.showRetry = true;
            }
        },
        selectThisRecording() {
            if (this.selectedStorage && this.selectedStorage.recordId) {
                this.$emit('recording-selected', this.selectedStorage.recordId);
            }
        },
        async handleDownloadRecording() {
            const cast_id = this.recording.pub_id;
            const start_time = this.recording["Start Time (Readable)"];
            const l_time = this.recording["End Time (Readable)"];
            const url = `${constants.apiCastUrl}/api/v1/decast/recording/download/?start_time=${encodeURIComponent(start_time)}&l_time=${encodeURIComponent(l_time)}&pub_id=${cast_id}`;


            try {
                const response = await api.get(url);
                const fileUrl = response.data.result.file_url;

                if (!fileUrl) throw new Error('File URL not found in response.');

                //TODO ----BELOW LOGIC FOR DOWNLOAD RECORDING IS NOT WORKING FOR HTTP----
                const fileResponse = await api.get(fileUrl, { responseType: 'blob' });
                const contentType = fileResponse.headers['content-type'];
                const blob = new Blob([fileResponse.data], { type: contentType });

                const downloadUrl = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = downloadUrl;
                a.download = `${cast_id}.m4v`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(downloadUrl);

                this.$vs.notify({
                    title: 'Download Started',
                    text: 'Your recording is being downloaded to your browser.',
                    color: 'success',
                });
            } catch (error) {
                console.error("Download error:", error);
                this.$vs.notify({
                    title: 'Download Failed',
                    text: error.message || 'There was an error while downloading the recording.',
                    color: 'danger',
                });
            } finally {
                document.getElementById('loading-bg-transparent').style.display = 'none';
            }
        }
        ,

        truncateText(text, maxLength) {
            if (text.length > maxLength) {
                return text.slice(0, maxLength) + "...";
            } else {
                return text;
            }
        },
    },
};
</script>

<style scoped>
* {
    font-family: "JetBrains Mono", monospace !important;
}

.processing-animation {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 24px;
}

.dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #d7df23;
    margin: 0 3px;
    animation: bounce 1.4s infinite ease-in-out both;
}

.dot:nth-child(1) {
    animation-delay: -0.32s;
}

.dot:nth-child(2) {
    animation-delay: -0.16s;
}

@keyframes bounce {

    0%,
    80%,
    100% {
        transform: scale(0);
    }

    40% {
        transform: scale(1);
    }
}

.retry-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    color: #d7df23;
    transition: transform 0.3s ease;
}

.retry-button:hover {
    transform: rotate(180deg);
}

.custm-more-icon {
    background-color: transparent !important;
    border: none !important;
}

.recordings {
    border-top: 1px solid white;
    border-left: 1px solid white;
    border-right: 2px solid white;
    border-bottom: 2px solid white;
    box-shadow: 3px 3px 0px 0px #a6a6a6;
}

.recordings:hover {
    border-top: 1px solid #D7DF23;
    border-left: 1px solid #D7DF23;
    border-right: 2px solid #D7DF23;
    border-bottom: 2px solid #D7DF23;
    box-shadow: 3px 3px 0px 0px #D7DF23;
}

.custm-style {
    background: none !important;
    outline: none !important;
    border: none !important;
    background-color: transparent !important;
}

.custm-style:hover {
    box-shadow: none !important;
}

.select_card {
    /* border: 1px solid #fff; */
    min-height: 220px;
    height: auto;
    width: 435px;
    display: flex;
}

.sia_container,
.swarm_container {
    border: 1px solid #fff;
    width: 100%;
}

.swarm_container:hover {
    border: 1px solid #d7df23;
    width: 100%;
}

.sia_container:hover {
    border: 1px solid #d7df23;
    width: 100%;
}

.loader {
    border: 4px solid #f3f3f3;
    /* Light gray */
    border-top: 4px solid #d7df23;
    /* Highlight color */
    border-radius: 50%;
    width: 24px;
    height: 24px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}
</style>